import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft, Trash2 } from 'lucide-react';
import { currentUser } from '~/lib/pb';
import { useActiveTenantId, useTenantContext } from '~/lib/tenantContext';
import {
  useBookingPage,
  useUpdateBookingPage,
  useDeleteBookingPage,
  type BookingPageInput,
} from '~/lib/booking-queries';
import { BookingPageForm } from '~/components/BookingPageForm/BookingPageForm';
import { useConfirm } from '~/components/ConfirmDialog/ConfirmDialog';
import styles from './_authed.agenda.editor.module.css';

export const Route = createFileRoute('/_authed/agenda_/$pageId')({
  component: EditBookingPage,
});

function EditBookingPage() {
  const { pageId } = Route.useParams();
  const me = currentUser();
  const tenantId = useActiveTenantId();
  const { selectedTenant } = useTenantContext();
  const isSuperadmin = me?.role === 'superadmin';
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { data: page, isLoading, isError } = useBookingPage(pageId);
  const update = useUpdateBookingPage();
  const del = useDeleteBookingPage();

  if (!isSuperadmin) {
    return (
      <div className={styles.page}>
        <p className={styles.muted}>Las páginas de reserva las configura Life Focus.</p>
      </div>
    );
  }
  if (!tenantId) {
    return (
      <div className={styles.page}>
        <p className={styles.muted}>Selecciona un cliente en el topbar.</p>
      </div>
    );
  }
  if (isLoading) {
    return (
      <div className={styles.page}>
        <p className={styles.muted}>Cargando…</p>
      </div>
    );
  }
  if (isError || !page || page.tenant !== tenantId) {
    return (
      <div className={styles.page}>
        <Link to="/agenda" className={styles.back}>
          <ArrowLeft size={16} aria-hidden /> Agenda
        </Link>
        <p className={styles.muted}>No encontramos esta página de reserva.</p>
      </div>
    );
  }

  async function handleSave(form: BookingPageInput) {
    await update.mutateAsync({ id: pageId, ...form });
  }

  async function handleDelete() {
    const ok = await confirm({
      title: `Eliminar "${page!.name}"`,
      description: (
        <>Se elimina la página de reserva y sus reservas asociadas. Acción irreversible.</>
      ),
      confirmLabel: 'Eliminar',
      tone: 'danger',
    });
    if (!ok) return;
    await del.mutateAsync(pageId);
    navigate({ to: '/agenda' });
  }

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <Link to="/agenda" className={styles.back}>
          <ArrowLeft size={16} aria-hidden /> Agenda
        </Link>
        <div className={styles.titleRow}>
          <h1 className="h2">{page.name}</h1>
          <button
            type="button"
            className="btn btn--ghost"
            onClick={() => void handleDelete()}
            disabled={del.isPending}
          >
            <Trash2 size={14} aria-hidden /> Eliminar
          </button>
        </div>
      </header>

      <BookingPageForm
        key={page.id}
        tenantId={tenantId}
        defaultTz={selectedTenant?.timezone || 'America/Santiago'}
        page={page}
        tenantSlug={selectedTenant?.slug || ''}
        saving={update.isPending}
        onSave={handleSave}
        onCancel={() => navigate({ to: '/agenda' })}
      />
    </div>
  );
}
